'use client'

import React, { useMemo } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { AlertCircle } from 'lucide-react'
import { projectFormSchema } from '../schemas'
import {
  designLevelOptions,
  engagementTypeOptions,
  materialsOptions,
  projectDomainOptions,
  weeklyHoursOptions,
} from '../constants'

interface FormData {
  projectDomain: string
  neededRoles: string[]
  projectServiceName: string
  shortSummary: string
  projectType?: string
  resultPreset?: string
  collaborationMode?: 'TEAM' | 'INDIVIDUAL'
  functionality: string
  mainCategories: string[]
  subCategories: string[]
  skills: string[]
  workType: string
  period: string
  startDate: string
  cost: string
  contactPhone: string
  contactEmail: string
  referenceService: string
  otherRequirements: string
  designLevel: string
  engagementType: string
  weeklyHours: string
  materials: string[]
}

interface ReviewStepProps {
  formData: FormData
  selectedFiles: File[]
  onSubmit: () => Promise<number | null>
  onPrevious: () => void
  loading?: boolean
}

const PROJECT_TYPE_LABELS: Record<string, string> = {
  customer_service: '판매/고객 대상 서비스',
  internal: '내부 운영 개선',
  mvp: 'MVP/검증용 개발',
  other: '기타 목적',
  web: '웹 서비스',
  mobile: '모바일 앱',
}

const WORK_TYPE_LABELS: Record<string, string> = {
  ONSITE: '대면근무',
  REMOTE: '원격근무',
  HYBRID: '원격+대면',
}



const PROJECT_DOMAIN_LABELS: Record<string, string> = Object.fromEntries(
  projectDomainOptions.map((option) => [option.value, option.label])
)

const DESIGN_LEVEL_LABELS: Record<string, string> = Object.fromEntries(
  designLevelOptions.map((option) => [option.value, option.label])
)

const ENGAGEMENT_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  engagementTypeOptions.map((option) => [option.value, option.label])
)

const WEEKLY_HOURS_LABELS: Record<string, string> = Object.fromEntries(
  weeklyHoursOptions.map((option) => [option.value, option.label])
)

const MATERIAL_LABELS: Record<string, string> = Object.fromEntries(
  materialsOptions.map((option) => [option.value, option.label])
)

const FIELD_LABELS: Record<string, string> = {
  projectServiceName: '프로젝트 이름',
  functionality: '필요한 기능',
  period: '예상 기간',
  cost: '예상 예산',
  contactPhone: '전화번호',
  contactEmail: '이메일',
}

type SectionItem = {
  label: string
  value?: React.ReactNode
  emptyFallback?: React.ReactNode
  hidden?: boolean
}

type Section = {
  title: string
  step: number
  fieldKeys: string[]
  items: SectionItem[]
}

function formatKoDate(iso: string) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('ko-KR')
}

function formatPhoneNumber(value: string) {
  const digits = value.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('010')) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`
  }
  if (digits.length === 10 && digits.startsWith('010')) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return value
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
  formData,
  selectedFiles,
  loading = false,
}) => {
  const router = useRouter()
  const searchParams = useSearchParams()

  const validationResult = useMemo(() => projectFormSchema.safeParse(formData), [formData])
  const isFormValid = validationResult.success

  const validationErrors = useMemo(() => {
    if (validationResult.success) return {}
    const fieldErrors = validationResult.error.flatten().fieldErrors
    const errors: Record<string, string> = {}
    Object.keys(fieldErrors).forEach((key) => {
      const arr = fieldErrors[key as keyof typeof fieldErrors]
      if (arr?.[0]) errors[key] = arr[0]
    })
    return errors
  }, [validationResult])

  const missingRequiredFields = useMemo(() => {
    if (validationResult.success) return []
    return Object.keys(validationErrors)
  }, [validationResult, validationErrors])

  const handleEdit = (targetStep: number) => {
    const params = new URLSearchParams(searchParams?.toString() || '')
    params.set('step', String(targetStep))
    router.push(`/client/projects/new?${params.toString()}`)
  }

  const sections: Section[] = useMemo(() => {
    const projectTypeLabel = formData.projectType
      ? PROJECT_TYPE_LABELS[formData.projectType] || formData.projectType
      : undefined

    const workTypeLabel = formData.workType
      ? WORK_TYPE_LABELS[formData.workType] || formData.workType
      : undefined

    const projectDomainLabel = formData.projectDomain
      ? PROJECT_DOMAIN_LABELS[formData.projectDomain] || formData.projectDomain
      : undefined

    const designLevelLabel = formData.designLevel
      ? DESIGN_LEVEL_LABELS[formData.designLevel] || formData.designLevel
      : undefined

    const engagementTypeLabel = formData.engagementType
      ? ENGAGEMENT_TYPE_LABELS[formData.engagementType] || formData.engagementType
      : undefined

    const weeklyHoursLabel = formData.weeklyHours
      ? WEEKLY_HOURS_LABELS[formData.weeklyHours] || formData.weeklyHours
      : undefined

    return [
      {
        title: '기본 정보',
        step: 1,
        fieldKeys: ['projectDomain', 'projectServiceName', 'shortSummary'],
        items: [
          {
            label: '프로젝트 분야',
            value: projectDomainLabel || <span className="text-gray-500">미입력</span>,
          },
          {
            label: '프로젝트명',
            value: formData.projectServiceName,
            emptyFallback: <span className="text-gray-500">미입력</span>,
          },
          {
            label: '한 줄 요약',
            value: formData.shortSummary || <span className="text-gray-500">미입력</span>,
          },
          {
            label: '프로젝트 유형',
            value: projectTypeLabel,
            hidden: !projectTypeLabel,
          },
        ],
      },
      {
        title: '작업 범위',
        step: 2,
        fieldKeys: ['functionality', 'designLevel', 'engagementType', 'weeklyHours'],
        items: [
          {
            label: '상세 브리프',
            value: (
              <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">
                {formData.functionality || '미입력'}
              </p>
            ),
          },
          {
            label: '서비스 유형',
            value:
              formData.mainCategories?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {formData.mainCategories.map((category) => (
                    <span
                      key={category}
                      className="px-3 py-1 bg-blue-50 border border-blue-200 text-blue-600 rounded-full text-xs font-medium"
                    >
                      {category}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-gray-500">미입력</span>
              ),
          },
          {
            label: '세부 카테고리',
            hidden: !(formData.subCategories?.length > 0),
            value: (
              <div className="flex flex-wrap gap-2">
                {formData.subCategories.map((category) => (
                  <span
                    key={category}
                    className="px-3 py-1 bg-gray-50 border border-gray-200 text-gray-700 rounded-full text-xs"
                  >
                    {category}
                  </span>
                ))}
              </div>
            ),
          },
          {
            label: '필요 스킬',
            hidden: !(formData.skills?.length > 0),
            value: (
              <div className="flex flex-wrap gap-2">
                {formData.skills.map((skill) => (
                  <span
                    key={skill}
                    className="px-3 py-1 bg-gray-50 border border-gray-200 text-gray-700 rounded-full text-xs"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            ),
          },
          {
            label: '요구되는 숙련도',
            value: designLevelLabel || <span className="text-gray-500">미입력</span>,
          },
          {
            label: '이미 보유한 자료',
            value: formData.materials?.length ? formData.materials.map((item) => MATERIAL_LABELS[item] || item).join(', ') : <span className="text-gray-500">미입력</span>,
          },
          {
            label: '진행 방식',
            value: workTypeLabel || <span className="text-gray-500">미입력</span>,
          },
          {
            label: '진행 형태',
            value: engagementTypeLabel || <span className="text-gray-500">미입력</span>,
          },
          {
            label: '주간 예상 투입',
            hidden: !formData.weeklyHours,
            value: weeklyHoursLabel || <span className="text-gray-500">미입력</span>,
          },
          {
            label: '레퍼런스 링크',
            hidden: !formData.referenceService,
            value: <p className="text-sm text-gray-600 break-words">{formData.referenceService}</p>,
          },
          {
            label: '추가 요청사항',
            hidden: !formData.otherRequirements,
            value: (
              <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">
                {formData.otherRequirements}
              </p>
            ),
          },
        ],
      },
      {
        title: '예산 / 일정',
        step: 3,
        fieldKeys: ['period', 'cost', 'startDate'],
        items: [
          { label: '희망 기간', value: formData.period || <span className="text-gray-500">미입력</span> },
          {
            label: '착수 희망일',
            value: formData.startDate ? formatKoDate(formData.startDate) : <span className="text-gray-500">미입력</span>,
          },
          {
            label: '예산 범위',
            value: (
              <span>
                {formData.cost || <span className="text-gray-500">미입력</span>}
                <span className="ml-1 text-xs text-gray-400">(VAT 포함 여부는 제안 단계에서 안내)</span>
              </span>
            ),
          },
        ],
      },
      {
        title: '상담 정보',
        step: 4,
        fieldKeys: ['contactPhone', 'contactEmail'],
        items: [
          { label: '전화번호', value: formatPhoneNumber(formData.contactPhone) || <span className="text-gray-500">미입력</span> },
          { label: '이메일', value: formData.contactEmail || <span className="text-gray-500">미입력</span> },
          {
            label: '첨부 파일',
            hidden: selectedFiles.length === 0,
            value: (
              <div className="space-y-1">
                {selectedFiles.map((file, index) => (
                  <div key={`${file.name}-${index}`} className="text-sm text-gray-600">
                    • {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                ))}
              </div>
            ),
          },
        ],
      },
    ]
  }, [formData, selectedFiles])

  const sectionStatuses = useMemo(() => {
    return sections.map((section) => {
      const errorCount = section.fieldKeys.filter((key) => validationErrors[key]).length
      return {
        ...section,
        errorCount,
        isComplete: errorCount === 0,
      }
    })
  }, [sections, validationErrors])

  return (
    <div className="w-full space-y-8">
      <header className="space-y-2">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">입력 내용을 확인하고 바로 제출하세요</h2>
        <p className="text-sm text-gray-600 leading-relaxed">
          확인이 끝나면 아래 <span className="text-blue-600 font-medium">프로젝트 등록 완료</span> 버튼으로 바로 제출할 수 있습니다.
        </p>
      </header>

      <section className="rounded-xl border border-blue-100 bg-blue-50/60 p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">핵심 요약</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <p><span className="text-gray-500">프로젝트명</span><br />{formData.projectServiceName || '미입력'}</p>
          <p><span className="text-gray-500">유형</span><br />{formData.projectType ? (PROJECT_TYPE_LABELS[formData.projectType] || formData.projectType) : '미입력'}</p>
          <p><span className="text-gray-500">예상 기간</span><br />{formData.period || '미입력'}</p>
          <p><span className="text-gray-500">예상 예산</span><br />{formData.cost || '미입력'}</p>
          <p><span className="text-gray-500">착수 희망일</span><br />{formData.startDate ? formatKoDate(formData.startDate) : '미정'}</p>
          <p><span className="text-gray-500">근무 형태</span><br />{formData.workType ? (WORK_TYPE_LABELS[formData.workType] || formData.workType) : '미입력'}</p>
        </div>
      </section>

      {!isFormValid && missingRequiredFields.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-900">수정이 필요한 항목 {missingRequiredFields.length}개</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-red-600">
                {missingRequiredFields.map((field) => (
                  <li key={field}>
                    <button type="button" className="underline underline-offset-2" onClick={() => handleEdit(field === 'contactPhone' || field === 'contactEmail' ? 4 : field === 'period' || field === 'cost' ? 3 : field === 'functionality' ? 2 : 1)}>
                      {validationErrors[field] || `${FIELD_LABELS[field] || field} 항목을 입력해주세요.`}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {sectionStatuses.map((sec) => (
          <section
            key={sec.title}
            className={[
              'bg-white border rounded-xl p-4 space-y-4',
              sec.isComplete ? 'border-gray-200' : 'border-red-300 bg-red-50/30',
            ].join(' ')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-gray-900">{sec.title}</h3>
                <span
                  className={[
                    'text-xs px-2 py-0.5 rounded-full font-medium',
                    sec.isComplete ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-100 text-red-700 border border-red-200',
                  ].join(' ')}
                >
                  {sec.isComplete ? '완료' : `미완료 (${sec.errorCount})`}
                </span>
              </div>
              <button
                type="button"
                onClick={() => handleEdit(sec.step)}
                className="text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                수정하기
              </button>
            </div>

            <div className="space-y-3">
              {sec.items
                .filter((it) => !it.hidden)
                .map((it, idx) => (
                  <div key={`${sec.title}-${idx}`} className="space-y-1">
                    <p className="text-xs text-gray-500">{it.label}</p>
                    <div className="text-sm text-gray-900">
                      {it.value ?? it.emptyFallback ?? <span className="text-gray-500">미입력</span>}
                    </div>
                  </div>
                ))}
            </div>
          </section>
        ))}
      </div>

    </div>
  )
}
